/**
 * The dashboard keeps its active tab in the URL hash, e.g. `https://app.lingoping.com/#decks`,
 * so a reload stays on the same tab and other screens can deep-link to one.
 */
export const OPTIONS_TABS = [
  'dashboard',
  'decks',
  'known',
  'cards',
  'settings',
  // Not a tab in the bar: the Subscribe page, reachable from every Subscribe button.
  'subscribe',
] as const;

export type OptionsTab = (typeof OPTIONS_TABS)[number];

export const DEFAULT_OPTIONS_TAB: OptionsTab = 'dashboard';

export function isOptionsTab(value: string): value is OptionsTab {
  return (OPTIONS_TABS as readonly string[]).includes(value);
}

/** Tab for a location hash (`#decks`, `decks`); the default tab for a missing or unknown one.
 * A tab may carry a detail after a slash (`#decks/<deck id>`): only the part before it names the tab. */
export function tabFromHash(hash: string): OptionsTab {
  const key = hash.replace(/^#/, '').split('/')[0].trim().toLowerCase();
  return isOptionsTab(key) ? key : DEFAULT_OPTIONS_TAB;
}

const DECK_ID = /^[0-9a-zA-Z_-]{1,64}$/;

/** The deck open on the Decks tab (`#decks/<id>`), or null when the hash is not a deck's. */
export function deckIdFromHash(hash: string): string | null {
  const [tab, id] = hash.replace(/^#/, '').split('/');
  if (tab?.trim().toLowerCase() !== 'decks' || !id) return null;
  return DECK_ID.test(id) ? id : null;
}

/** Location hash for an open deck, e.g. `#decks/4f1c…`. */
export function hashForDeck(deckId: string): string {
  return `#decks/${deckId}`;
}

/** Location hash for a tab, e.g. `#decks`. */
export function hashForTab(tab: OptionsTab): string {
  return `#${tab}`;
}
